/**
 * 来場者屋台ランキングサービス
 * 動的な評価項目・公開屋台・評価済み状態の取得と、評価送信による EXP 取得を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import {
  isValidStallRatingAccessToken,
  normalizeStallRatingAccessToken,
} from '../../../shared/utils/stallRatingQr';
import { validateRequiredString } from '../../../shared/utils/validation';
import { isValidAnonymousStallRatingDeviceToken } from './anonymousStallRatingDeviceService';
import { invalidateVisitorHomeBootstrapCache } from './visitorHomeBootstrapCache';

/** 状態取得 RPC 名 */
const VISITOR_GET_STALL_RATING_STATUS_RPC_NAME = 'visitor_get_stall_rating_status_v2';

/** 評価送信 RPC 名 */
const VISITOR_SUBMIT_STALL_RATING_RPC_NAME = 'visitor_submit_stall_rating';

/** 匿名状態取得 RPC 名 */
const VISITOR_GET_ANONYMOUS_STALL_RATING_STATUS_RPC_NAME =
  'visitor_get_anonymous_stall_rating_status_v2';

/** sourceが返す屋台番号のASCII英数字形式。 */
const STALL_NUMBER_PATTERN = /^[A-Za-z0-9]{1,20}$/u;

/** 匿名評価送信 RPC 名 */
const VISITOR_SUBMIT_ANONYMOUS_STALL_RATING_RPC_NAME =
  'visitor_submit_anonymous_stall_rating';

/** 匿名ptコード再発行 RPC 名 */
const VISITOR_REISSUE_ANONYMOUS_STALL_RATING_REWARD_CODE_RPC_NAME =
  'visitor_reissue_anonymous_stall_rating_reward_code_v2';

/** 匿名回答ptコード交換 RPC 名 */
const VISITOR_CLAIM_STALL_RATING_REWARD_RPC_NAME =
  'visitor_claim_stall_rating_reward';

/** 感想コメントの最大文字数 */
export const STALL_RATING_COMMENT_MAX_LENGTH = 500;

/** 入力不正エラーコード */
export const VISITOR_STALL_RATING_INVALID_INPUT_CODE = 'VISITOR_STALL_RATING_INVALID_INPUT';

/** セッション不正エラーコード */
export const VISITOR_STALL_RATING_SESSION_INVALID_CODE = 'VISITOR_STALL_RATING_SESSION_INVALID';

/** 機能停止エラーコード */
export const VISITOR_STALL_RATING_DISABLED_CODE = 'VISITOR_STALL_RATING_DISABLED';

/** 屋台未検出エラーコード */
export const VISITOR_STALL_RATING_NOT_FOUND_CODE = 'VISITOR_STALL_RATING_NOT_FOUND';

/** 評価値不正エラーコード */
export const VISITOR_STALL_RATING_INVALID_RATING_CODE = 'VISITOR_STALL_RATING_INVALID_RATING';

/** 感想コメント不正エラーコード */
export const VISITOR_STALL_RATING_INVALID_COMMENT_CODE = 'VISITOR_STALL_RATING_INVALID_COMMENT';

/** 評価済みエラーコード */
export const VISITOR_STALL_RATING_ALREADY_RATED_CODE = 'VISITOR_STALL_RATING_ALREADY_RATED';

/** QRまたは手入力コードのアクセス不正エラーコード */
export const VISITOR_STALL_RATING_ACCESS_INVALID_CODE = 'VISITOR_STALL_RATING_ACCESS_INVALID';

/** 状態取得失敗エラーコード */
export const VISITOR_STALL_RATING_FETCH_FAILED_CODE = 'VISITOR_STALL_RATING_FETCH_FAILED';

/** 評価送信失敗エラーコード */
export const VISITOR_STALL_RATING_SUBMIT_FAILED_CODE = 'VISITOR_STALL_RATING_SUBMIT_FAILED';

/** 匿名端末トークン不正エラーコード */
export const VISITOR_STALL_RATING_INVALID_DEVICE_TOKEN_CODE = 'INVALID_DEVICE_TOKEN';

/** 匿名送信ID不正エラーコード */
export const VISITOR_STALL_RATING_INVALID_SUBMISSION_ID_CODE = 'INVALID_SUBMISSION_ID';

/** 匿名回答済みエラーコード */
export const VISITOR_STALL_RATING_ANONYMOUS_ALREADY_RATED_CODE =
  'ANONYMOUS_STALL_ALREADY_RATED';

/** 匿名送信競合エラーコード */
export const VISITOR_STALL_RATING_ANONYMOUS_SUBMISSION_CONFLICT_CODE =
  'ANONYMOUS_SUBMISSION_CONFLICT';

/** 匿名評価未検出エラーコード */
export const VISITOR_STALL_RATING_ANONYMOUS_NOT_FOUND_CODE =
  'ANONYMOUS_RATING_NOT_FOUND';

/** ptコード交換期間終了エラーコード */
export const VISITOR_STALL_RATING_REWARD_PERIOD_ENDED_CODE =
  'STALL_RATING_REWARD_PERIOD_ENDED';

/** ptコード未検出エラーコード */
export const VISITOR_STALL_RATING_REWARD_NOT_FOUND_CODE =
  'STALL_RATING_REWARD_NOT_FOUND';

/** ptコード期限切れエラーコード */
export const VISITOR_STALL_RATING_REWARD_EXPIRED_CODE =
  'STALL_RATING_REWARD_EXPIRED';

/** ptコード失効エラーコード */
export const VISITOR_STALL_RATING_REWARD_REVOKED_CODE =
  'STALL_RATING_REWARD_REVOKED';

/** ptコード交換済みエラーコード */
export const VISITOR_STALL_RATING_REWARD_ALREADY_CLAIMED_CODE =
  'STALL_RATING_REWARD_ALREADY_CLAIMED';

/** ptコード交換競合エラーコード */
export const VISITOR_STALL_RATING_REWARD_CONFLICT_CODE =
  'STALL_RATING_REWARD_CONFLICT';

/** レート制限エラーコード */
export const VISITOR_STALL_RATING_RATE_LIMITED_CODE = 'RATE_LIMIT_EXCEEDED';

/** レスポンス不正エラーコード */
const VISITOR_STALL_RATING_INVALID_RESPONSE_CODE = 'VISITOR_STALL_RATING_INVALID_RESPONSE';

/**
 * RPC エラーに指定文字列が含まれるかを判定する
 * @param {{ message?: string } | null} rpcError - RPC エラー
 * @param {string} keyword - 判定文字列
 * @returns {boolean} 含まれる場合 true
 */
const includesRpcErrorMessage = (rpcError, keyword) => {
  return Boolean(rpcError?.message?.includes(keyword));
};

/**
 * RPC エラーを来場者向けエラーコードへ変換する
 * @param {{ message?: string } | null} rpcError - RPC エラー
 * @param {string} fallbackCode - 未知エラー時のコード
 * @returns {string} アプリ向けエラーコード
 */
const mapStallRatingRpcErrorCode = (rpcError, fallbackCode) => {
  if (includesRpcErrorMessage(rpcError, 'INVALID_SESSION_TOKEN')) {
    return VISITOR_STALL_RATING_INVALID_INPUT_CODE;
  }
  if (
    includesRpcErrorMessage(rpcError, 'SESSION_NOT_FOUND')
    || includesRpcErrorMessage(rpcError, 'VISITOR_INACTIVE')
  ) {
    return VISITOR_STALL_RATING_SESSION_INVALID_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_RATING_DISABLED')) {
    return VISITOR_STALL_RATING_DISABLED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_NOT_FOUND')) {
    return VISITOR_STALL_RATING_NOT_FOUND_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'INVALID_COMMENT')) {
    return VISITOR_STALL_RATING_INVALID_COMMENT_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'INVALID_RATING')) {
    return VISITOR_STALL_RATING_INVALID_RATING_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'ANONYMOUS_STALL_ALREADY_RATED')) {
    return VISITOR_STALL_RATING_ANONYMOUS_ALREADY_RATED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_ALREADY_RATED')) {
    return VISITOR_STALL_RATING_ALREADY_RATED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'ANONYMOUS_SUBMISSION_CONFLICT')) {
    return VISITOR_STALL_RATING_ANONYMOUS_SUBMISSION_CONFLICT_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'ANONYMOUS_RATING_NOT_FOUND')) {
    return VISITOR_STALL_RATING_ANONYMOUS_NOT_FOUND_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'INVALID_DEVICE_TOKEN')) {
    return VISITOR_STALL_RATING_INVALID_DEVICE_TOKEN_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'INVALID_SUBMISSION_ID')) {
    return VISITOR_STALL_RATING_INVALID_SUBMISSION_ID_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_RATING_REWARD_PERIOD_ENDED')) {
    return VISITOR_STALL_RATING_REWARD_PERIOD_ENDED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_RATING_REWARD_NOT_FOUND')) {
    return VISITOR_STALL_RATING_REWARD_NOT_FOUND_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_RATING_REWARD_EXPIRED')) {
    return VISITOR_STALL_RATING_REWARD_EXPIRED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_RATING_REWARD_REVOKED')) {
    return VISITOR_STALL_RATING_REWARD_REVOKED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_RATING_REWARD_ALREADY_CLAIMED')) {
    return VISITOR_STALL_RATING_REWARD_ALREADY_CLAIMED_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'STALL_RATING_REWARD_CONFLICT')) {
    return VISITOR_STALL_RATING_REWARD_CONFLICT_CODE;
  }
  if (includesRpcErrorMessage(rpcError, 'RATE_LIMIT_EXCEEDED')) {
    return VISITOR_STALL_RATING_RATE_LIMITED_CODE;
  }
  if (
    rpcError?.code === 'PGRST202'
    || includesRpcErrorMessage(rpcError, 'Could not find the function')
    || includesRpcErrorMessage(rpcError, 'STALL_QR_ACCESS_INVALID')
  ) {
    return VISITOR_STALL_RATING_ACCESS_INVALID_CODE;
  }
  return fallbackCode;
};

/**
 * エラーコードに対応する来場者向けメッセージを返す
 * @param {string} errorCode - アプリ向けエラーコード
 * @param {string} fallbackMessage - 未知エラー時のメッセージ
 * @returns {string} 表示メッセージ
 */
const resolveStallRatingErrorMessage = (errorCode, fallbackMessage) => {
  if (errorCode === VISITOR_STALL_RATING_SESSION_INVALID_CODE) {
    return 'セッションの有効期限が切れました。もう一度ログインしてください。';
  }
  if (errorCode === VISITOR_STALL_RATING_DISABLED_CODE) {
    return '屋台ランキング機能は現在利用できません。';
  }
  if (errorCode === VISITOR_STALL_RATING_NOT_FOUND_CODE) {
    return '公開中の屋台が見つかりません。屋台番号を確認してください。';
  }
  if (errorCode === VISITOR_STALL_RATING_INVALID_RATING_CODE) {
    return 'すべての評価項目を1〜5で選んでください。';
  }
  if (errorCode === VISITOR_STALL_RATING_INVALID_COMMENT_CODE) {
    return '感想は500文字以内で入力してください。';
  }
  if (errorCode === VISITOR_STALL_RATING_ALREADY_RATED_CODE) {
    return 'この屋台はすでに評価済みです';
  }
  if (errorCode === VISITOR_STALL_RATING_ANONYMOUS_ALREADY_RATED_CODE) {
    return 'この端末では、この開催回の屋台ランキングに回答済みです。';
  }
  if (errorCode === VISITOR_STALL_RATING_ANONYMOUS_SUBMISSION_CONFLICT_CODE) {
    return '送信内容を確認できませんでした。画面を読み込み直して回答状況を確認してください。';
  }
  if (errorCode === VISITOR_STALL_RATING_REWARD_PERIOD_ENDED_CODE) {
    return 'pt受取コードの発行期間は終了しました。';
  }
  if (errorCode === VISITOR_STALL_RATING_REWARD_NOT_FOUND_CODE) {
    return 'pt受取コードを確認できませんでした。入力内容を確認してください。';
  }
  if (errorCode === VISITOR_STALL_RATING_REWARD_EXPIRED_CODE) {
    return 'このpt受取コードは有効期限が切れています。';
  }
  if (errorCode === VISITOR_STALL_RATING_REWARD_REVOKED_CODE) {
    return 'このpt受取コードは再発行により無効になっています。最新のコードを入力してください。';
  }
  if (errorCode === VISITOR_STALL_RATING_REWARD_ALREADY_CLAIMED_CODE) {
    return 'このpt受取コードはすでに交換済みです。';
  }
  if (errorCode === VISITOR_STALL_RATING_REWARD_CONFLICT_CODE) {
    return 'pt受取処理が重複しました。所持ptを確認してください。';
  }
  if (errorCode === VISITOR_STALL_RATING_RATE_LIMITED_CODE) {
    return '短時間に操作が集中しています。少し待ってからもう一度お試しください。';
  }
  if (errorCode === VISITOR_STALL_RATING_ACCESS_INVALID_CODE) {
    return 'この屋台ランキングQRまたは8文字コードは利用できません。屋台にある最新の案内を確認してください。';
  }
  return fallbackMessage;
};

/**
 * JSON 配列または配列値を安全に配列へ変換する
 * @param {unknown} value - 変換対象
 * @returns {Array} 配列
 */
const parseArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return [];
  }

  try {
    /** JSON として復元した値 */
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    logError('屋台ランキング状態の配列レスポンス解析に失敗しました。', {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
};

/**
 * 評価値が星1〜5の整数か検証する
 * @param {number} rating - 評価値
 * @returns {boolean} 妥当な場合 true
 */
const isValidRating = (rating) => {
  return Number.isInteger(rating) && rating >= 1 && rating <= 5;
};

/**
 * RPC の不正レスポンスをログへ記録して AppError を送出する
 * @param {string} context - ログコンテキスト
 * @throws {AppError} レスポンスが不正な場合
 */
const throwInvalidResponse = (context) => {
  logError(`${context} のレスポンス形式が不正です。`);
  throw new AppError('屋台ランキング情報の受信に失敗しました。', {
    code: VISITOR_STALL_RATING_INVALID_RESPONSE_CODE,
    statusCode: 500,
  });
};

/**
 * 来場者の屋台ランキング状態を取得する
 * @param {string} sessionToken - セッショントークン
 * @param {string} accessToken - 屋台ランキングQRトークンまたは8文字コード
 * @returns {Promise<{
 *  isEnabled: boolean,
 *  rewardPoints: number,
 *  stall: { id: string, stallNumber: string, name: string, description: string },
 *  isAlreadyRated: boolean,
 *  criteria: Array<{ id: string, label: string, sortOrder: number }>,
 *  accessExpiresAt: string,
 * }>} 屋台ランキング状態
 * @throws {AppError} 入力・通信・レスポンスエラー時
 */
export const visitorGetStallRatingStatus = async (sessionToken, accessToken) => {
  /** セッショントークン検証結果 */
  const sessionValidation = validateRequiredString(sessionToken, 'セッショントークン');
  if (!sessionValidation.isValid) {
    throw new AppError(sessionValidation.message, {
      code: VISITOR_STALL_RATING_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** QRトークンまたは大文字へ正規化した手入力コード */
  const normalizedAccessToken = normalizeStallRatingAccessToken(accessToken);

  if (!isValidStallRatingAccessToken(normalizedAccessToken)) {
    throw new AppError('屋台のQRを読み取るか、案内された8文字コードを入力してください。', {
      code: VISITOR_STALL_RATING_ACCESS_INVALID_CODE,
      statusCode: 403,
    });
  }

  /** Supabase クライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC 結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_GET_STALL_RATING_STATUS_RPC_NAME, {
    p_session_token: sessionToken.trim(),
    p_access_token: normalizedAccessToken,
  });

  if (error) {
    /** マップ済みエラーコード */
    const mappedErrorCode = mapStallRatingRpcErrorCode(error, VISITOR_STALL_RATING_FETCH_FAILED_CODE);
    logError('visitor_get_stall_rating_status_v2 RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });
    throw new AppError(
      resolveStallRatingErrorMessage(mappedErrorCode, '屋台ランキング情報の取得に失敗しました。'),
      {
        code: mappedErrorCode,
        statusCode: mappedErrorCode === VISITOR_STALL_RATING_SESSION_INVALID_CODE
          ? 401
          : mappedErrorCode === VISITOR_STALL_RATING_ACCESS_INVALID_CODE
            ? 403
            : 500,
        details: { message: error.message },
      },
    );
  }

  /** RPC 戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;
  if (!firstRow || typeof firstRow !== 'object') {
    throwInvalidResponse('visitor_get_stall_rating_status_v2 RPC');
  }

  /** QRで固定された対象屋台 */
  const stall = {
    id: String(firstRow.stall_id ?? ''),
    stallNumber: String(firstRow.stall_number ?? '').trim(),
    name: String(firstRow.stall_name ?? ''),
    description: String(firstRow.stall_description ?? ''),
  };

  if (!stall.id || !STALL_NUMBER_PATTERN.test(stall.stallNumber) || !stall.name) {
    throwInvalidResponse('visitor_get_stall_rating_status_v2 RPC');
  }

  /** 評価項目の生配列 */
  const criteriaRows = parseArrayValue(firstRow.criteria);

  /** 整形済み評価項目（sort_order昇順） */
  const criteria = criteriaRows
    .map((criterionRow) => ({
      id: String(criterionRow?.id ?? ''),
      label: String(criterionRow?.label ?? ''),
      sortOrder: Number(criterionRow?.sort_order ?? 0),
    }))
    .filter((criterion) => criterion.id && criterion.label)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return {
    isEnabled: Boolean(firstRow.is_enabled),
    rewardPoints: Number(firstRow.reward_points ?? 0),
    stall,
    isAlreadyRated: Boolean(firstRow.is_already_rated),
    criteria,
    accessExpiresAt: String(firstRow.access_expires_at ?? ''),
  };
};

/**
 * 来場者の屋台ランキング評価を送信して EXP を取得する
 * @param {string} sessionToken - セッショントークン
 * @param {string} accessToken - 屋台ランキングQRトークンまたは8文字コード
 * @param {Object} input - 評価入力
 * @param {Object<string, number>} input.scores - 評価項目ID をキーとした星評価(1〜5)
 * @param {string} [input.comment] - 生駒祭のかんそう（任意、500文字まで）
 * @returns {Promise<{
 *  ratingId: string,
 *  stallName: string,
 *  rewardPoints: number,
 *  currentPoint: number,
 *  maxReachedPoint: number,
 *  pointLogId: string,
 * }>} 評価登録と EXP 付与結果
 * @throws {AppError} 入力・状態・通信・レスポンスエラー時
 */
export const visitorSubmitStallRating = async (sessionToken, accessToken, input) => {
  /** セッショントークン検証結果 */
  const sessionValidation = validateRequiredString(sessionToken, 'セッショントークン');
  if (!sessionValidation.isValid) {
    throw new AppError(sessionValidation.message, {
      code: VISITOR_STALL_RATING_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** QRトークンまたは大文字へ正規化した手入力コード */
  const normalizedAccessToken = normalizeStallRatingAccessToken(accessToken);

  if (!isValidStallRatingAccessToken(normalizedAccessToken)) {
    throw new AppError('屋台のQRを読み取るか、案内された8文字コードを入力してください。', {
      code: VISITOR_STALL_RATING_ACCESS_INVALID_CODE,
      statusCode: 403,
    });
  }

  /** 評価項目ID→スコアのエントリ一覧 */
  const scoreEntries = Object.entries(input?.scores ?? {});

  if (
    scoreEntries.length === 0
    || !scoreEntries.every(([, score]) => isValidRating(Number(score)))
  ) {
    throw new AppError('すべての評価項目を1〜5で選んでください。', {
      code: VISITOR_STALL_RATING_INVALID_RATING_CODE,
      statusCode: 400,
    });
  }

  /** RPC送信用のスコア配列（criterion_id/score形式） */
  const scoresPayload = scoreEntries.map(([criterionId, score]) => ({
    criterion_id: criterionId,
    score: Number(score),
  }));

  /** 前後空白を除去した感想コメント */
  const normalizedComment = String(input?.comment ?? '').trim();

  if (normalizedComment.length > STALL_RATING_COMMENT_MAX_LENGTH) {
    throw new AppError('感想は500文字以内で入力してください。', {
      code: VISITOR_STALL_RATING_INVALID_COMMENT_CODE,
      statusCode: 400,
    });
  }

  /** Supabase クライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC 結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_SUBMIT_STALL_RATING_RPC_NAME, {
    p_session_token: sessionToken.trim(),
    p_access_token: normalizedAccessToken,
    p_scores: scoresPayload,
    p_comment: normalizedComment,
  });

  if (error) {
    /** マップ済みエラーコード */
    const mappedErrorCode = mapStallRatingRpcErrorCode(error, VISITOR_STALL_RATING_SUBMIT_FAILED_CODE);
    logError('visitor_submit_stall_rating RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });
    throw new AppError(
      resolveStallRatingErrorMessage(mappedErrorCode, '屋台ランキング評価の送信に失敗しました。'),
      {
        code: mappedErrorCode,
        statusCode: mappedErrorCode === VISITOR_STALL_RATING_SESSION_INVALID_CODE
          ? 401
          : mappedErrorCode === VISITOR_STALL_RATING_ACCESS_INVALID_CODE
            ? 403
            : 500,
        details: { message: error.message },
      },
    );
  }

  /** RPC 戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;
  if (!firstRow?.rating_id || !firstRow?.stall_name) {
    throwInvalidResponse('visitor_submit_stall_rating RPC');
  }

  // pt残高が変わるため、ホーム集約データのTTLキャッシュを破棄する
  invalidateVisitorHomeBootstrapCache(sessionToken.trim());

  return {
    ratingId: String(firstRow.rating_id),
    stallName: String(firstRow.stall_name),
    rewardPoints: Number(firstRow.reward_points ?? 0),
    currentPoint: Number(firstRow.current_point ?? 0),
    maxReachedPoint: Number(firstRow.max_reached_point ?? 0),
    pointLogId: String(firstRow.point_log_id ?? ''),
  };
};

/** UUID形式 */
const SUBMISSION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** 読み間違えやすい0/1/I/Oを除外した12文字pt受取コード形式 */
const STALL_RATING_REWARD_CODE_PATTERN = /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{12}$/;

/**
 * RPCエラーコードに対応するHTTP相当statusを返す
 * @param {string} errorCode - アプリ向けエラーコード
 * @returns {number} HTTP相当status
 */
const resolveStallRatingErrorStatus = (errorCode) => {
  if (errorCode === VISITOR_STALL_RATING_SESSION_INVALID_CODE) {
    return 401;
  }
  if (errorCode === VISITOR_STALL_RATING_ACCESS_INVALID_CODE) {
    return 403;
  }
  if (errorCode === VISITOR_STALL_RATING_RATE_LIMITED_CODE) {
    return 429;
  }
  if (
    errorCode === VISITOR_STALL_RATING_REWARD_EXPIRED_CODE
    || errorCode === VISITOR_STALL_RATING_REWARD_REVOKED_CODE
    || errorCode === VISITOR_STALL_RATING_REWARD_PERIOD_ENDED_CODE
  ) {
    return 410;
  }
  if (
    errorCode === VISITOR_STALL_RATING_ANONYMOUS_ALREADY_RATED_CODE
    || errorCode === VISITOR_STALL_RATING_ANONYMOUS_SUBMISSION_CONFLICT_CODE
    || errorCode === VISITOR_STALL_RATING_REWARD_ALREADY_CLAIMED_CODE
    || errorCode === VISITOR_STALL_RATING_REWARD_CONFLICT_CODE
  ) {
    return 409;
  }
  return 400;
};

/**
 * 匿名・ptコードRPCエラーを生の秘密値なしでAppErrorへ変換する
 * @param {string} context - RPCログコンテキスト
 * @param {{code?: string, message?: string} | null} error - Supabase RPCエラー
 * @param {string} fallbackCode - 未知エラーコード
 * @param {string} fallbackMessage - 未知エラーメッセージ
 * @throws {AppError} 常にアプリ向けエラーを送出する
 */
const throwMappedStallRatingRpcError = (
  context,
  error,
  fallbackCode,
  fallbackMessage,
) => {
  /** アプリ向けへマップしたエラーコード */
  const mappedErrorCode = mapStallRatingRpcErrorCode(error, fallbackCode);
  logError(`${context} RPC 実行に失敗しました。`, {
    code: error?.code,
    mappedErrorCode,
  });
  throw new AppError(
    resolveStallRatingErrorMessage(mappedErrorCode, fallbackMessage),
    {
      code: mappedErrorCode,
      statusCode: resolveStallRatingErrorStatus(mappedErrorCode),
    },
  );
};

/**
 * 匿名RPC共通入力を検証する
 * @param {string} accessToken - QRアクセストークン
 * @param {string} deviceToken - 匿名端末トークン
 * @returns {void}
 * @throws {AppError} 入力形式が不正な場合
 */
const validateAnonymousStallRatingAccess = (accessToken, deviceToken) => {
  if (!isValidStallRatingAccessToken(accessToken)) {
    throw new AppError('屋台のQRを読み取ってからアクセスしてください。', {
      code: VISITOR_STALL_RATING_ACCESS_INVALID_CODE,
      statusCode: 403,
    });
  }
  if (!isValidAnonymousStallRatingDeviceToken(deviceToken)) {
    throw new AppError('匿名回答用の端末情報が不正です。画面を読み込み直してください。', {
      code: VISITOR_STALL_RATING_INVALID_DEVICE_TOKEN_CODE,
      statusCode: 400,
    });
  }
};

/**
 * submission ID形式を検証する
 * @param {string} submissionId - 送信操作ID
 * @returns {void}
 * @throws {AppError} UUID形式が不正な場合
 */
const validateSubmissionId = (submissionId) => {
  if (!SUBMISSION_ID_PATTERN.test(String(submissionId ?? '').trim())) {
    throw new AppError('匿名回答の送信情報が不正です。画面を読み込み直してください。', {
      code: VISITOR_STALL_RATING_INVALID_SUBMISSION_ID_CODE,
      statusCode: 400,
    });
  }
};

/**
 * 評価入力をRPC payloadへ整形する
 * @param {Object} input - 評価入力
 * @returns {{scoresPayload: Array<{criterion_id: string, score: number}>, normalizedComment: string}} 整形済み入力
 * @throws {AppError} 評価・コメントが不正な場合
 */
const normalizeStallRatingInput = (input) => {
  /** 評価項目IDとスコアの一覧 */
  const scoreEntries = Object.entries(input?.scores ?? {});
  if (
    scoreEntries.length === 0
    || !scoreEntries.every(([, score]) => isValidRating(Number(score)))
  ) {
    throw new AppError('すべての評価項目を1〜5で選んでください。', {
      code: VISITOR_STALL_RATING_INVALID_RATING_CODE,
      statusCode: 400,
    });
  }

  /** 前後空白を除去した任意コメント */
  const normalizedComment = String(input?.comment ?? '').trim();
  if (normalizedComment.length > STALL_RATING_COMMENT_MAX_LENGTH) {
    throw new AppError('感想は500文字以内で入力してください。', {
      code: VISITOR_STALL_RATING_INVALID_COMMENT_CODE,
      statusCode: 400,
    });
  }

  return {
    scoresPayload: scoreEntries.map(([criterionId, score]) => ({
      criterion_id: criterionId,
      score: Number(score),
    })),
    normalizedComment,
  };
};

/**
 * 匿名端末向け屋台ランキング状態を取得する
 * @param {string} accessToken - 屋台ランキングQRアクセストークン
 * @param {string} deviceToken - 匿名端末トークン
 * @returns {Promise<Object>} 匿名回答状態
 * @throws {AppError} 入力・通信・レスポンスエラー時
 */
export const visitorGetAnonymousStallRatingStatus = async (
  accessToken,
  deviceToken,
) => {
  validateAnonymousStallRatingAccess(accessToken, deviceToken);
  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();
  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(
    VISITOR_GET_ANONYMOUS_STALL_RATING_STATUS_RPC_NAME,
    {
      p_access_token: accessToken.trim(),
      p_device_token: deviceToken,
    },
  );
  if (error) {
    throwMappedStallRatingRpcError(
      VISITOR_GET_ANONYMOUS_STALL_RATING_STATUS_RPC_NAME,
      error,
      VISITOR_STALL_RATING_FETCH_FAILED_CODE,
      '屋台ランキング情報の取得に失敗しました。',
    );
  }

  /** RPC戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;
  if (!firstRow || typeof firstRow !== 'object') {
    throwInvalidResponse(VISITOR_GET_ANONYMOUS_STALL_RATING_STATUS_RPC_NAME);
  }
  /** QRで固定された対象屋台 */
  const stall = {
    id: String(firstRow.stall_id ?? ''),
    stallNumber: String(firstRow.stall_number ?? '').trim(),
    name: String(firstRow.stall_name ?? ''),
    description: String(firstRow.stall_description ?? ''),
  };
  if (!stall.id || !STALL_NUMBER_PATTERN.test(stall.stallNumber) || !stall.name) {
    throwInvalidResponse(VISITOR_GET_ANONYMOUS_STALL_RATING_STATUS_RPC_NAME);
  }
  /** 評価項目の生配列 */
  const criteriaRows = parseArrayValue(firstRow.criteria);

  return {
    isEnabled: Boolean(firstRow.is_enabled),
    rewardPoints: Number(firstRow.reward_points ?? 0),
    stall,
    hasSubmitted: Boolean(firstRow.has_submitted),
    submittedStallId: String(firstRow.submitted_stall_id ?? ''),
    rewardCodeHint: String(firstRow.reward_code_hint ?? ''),
    rewardCodeStatus: String(firstRow.reward_code_status ?? 'not_issued'),
    criteria: criteriaRows
      .map((criterionRow) => ({
        id: String(criterionRow?.id ?? ''),
        label: String(criterionRow?.label ?? ''),
        sortOrder: Number(criterionRow?.sort_order ?? 0),
      }))
      .filter((criterion) => criterion.id && criterion.label)
      .sort((a, b) => a.sortOrder - b.sortOrder),
    accessExpiresAt: String(firstRow.access_expires_at ?? ''),
  };
};

/**
 * 匿名端末から屋台評価を送信しsecret-onceのptコードを受け取る
 * @param {string} accessToken - QRアクセストークン
 * @param {string} deviceToken - 匿名端末トークン
 * @param {string} submissionId - 送信操作UUID
 * @param {Object} input - 評価入力
 * @returns {Promise<Object>} 匿名評価・ptコード発行結果
 * @throws {AppError} 入力・状態・通信・レスポンスエラー時
 */
export const visitorSubmitAnonymousStallRating = async (
  accessToken,
  deviceToken,
  submissionId,
  input,
) => {
  validateAnonymousStallRatingAccess(accessToken, deviceToken);
  validateSubmissionId(submissionId);
  /** RPC向けに整形した評価入力 */
  const { scoresPayload, normalizedComment } = normalizeStallRatingInput(input);
  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();
  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(
    VISITOR_SUBMIT_ANONYMOUS_STALL_RATING_RPC_NAME,
    {
      p_access_token: accessToken.trim(),
      p_device_token: deviceToken,
      p_submission_id: submissionId.trim(),
      p_scores: scoresPayload,
      p_comment: normalizedComment || null,
    },
  );
  if (error) {
    throwMappedStallRatingRpcError(
      VISITOR_SUBMIT_ANONYMOUS_STALL_RATING_RPC_NAME,
      error,
      VISITOR_STALL_RATING_SUBMIT_FAILED_CODE,
      '屋台ランキング評価の送信に失敗しました。',
    );
  }

  /** RPC戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;
  /** 再送応答か */
  const isReplay = Boolean(firstRow?.is_replay);
  /** secret-onceで返されたptコード */
  const rewardCode = String(firstRow?.reward_code ?? '');
  if (
    !firstRow?.rating_id
    || !firstRow?.stall_id
    || !firstRow?.stall_name
    || (!isReplay && !STALL_RATING_REWARD_CODE_PATTERN.test(rewardCode))
  ) {
    throwInvalidResponse(VISITOR_SUBMIT_ANONYMOUS_STALL_RATING_RPC_NAME);
  }

  return {
    ratingId: String(firstRow.rating_id),
    stallId: String(firstRow.stall_id),
    stallName: String(firstRow.stall_name),
    rewardPoints: Number(firstRow.reward_points ?? 0),
    rewardCode: rewardCode || null,
    rewardCodeHint: String(firstRow.reward_code_hint ?? ''),
    rewardExpiresAt: String(firstRow.reward_expires_at ?? ''),
    isReplay,
  };
};

/**
 * 匿名評価の未交換ptコードを明示的に再発行する
 * @param {string} deviceToken - 匿名端末トークン
 * @param {string} submissionId - 再発行操作UUID
 * @param {string} stallId - 再発行対象の屋台ID
 * @returns {Promise<Object>} 新しいsecret-once ptコード
 * @throws {AppError} 入力・状態・通信・レスポンスエラー時
 */
export const visitorReissueAnonymousStallRatingRewardCode = async (
  deviceToken,
  submissionId,
  stallId,
) => {
  if (!isValidAnonymousStallRatingDeviceToken(deviceToken)) {
    throw new AppError('匿名回答用の端末情報が不正です。画面を読み込み直してください。', {
      code: VISITOR_STALL_RATING_INVALID_DEVICE_TOKEN_CODE,
      statusCode: 400,
    });
  }
  validateSubmissionId(submissionId);
  /** 再発行対象の屋台ID */
  const normalizedStallId = String(stallId ?? '').trim();
  if (!normalizedStallId) {
    throw new AppError('屋台情報を確認できませんでした。QRを読み込み直してください。', {
      code: VISITOR_STALL_RATING_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }
  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();
  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(
    VISITOR_REISSUE_ANONYMOUS_STALL_RATING_REWARD_CODE_RPC_NAME,
    {
      p_device_token: deviceToken,
      p_submission_id: submissionId.trim(),
      p_stall_id: normalizedStallId,
    },
  );
  if (error) {
    throwMappedStallRatingRpcError(
      VISITOR_REISSUE_ANONYMOUS_STALL_RATING_REWARD_CODE_RPC_NAME,
      error,
      VISITOR_STALL_RATING_SUBMIT_FAILED_CODE,
      'pt受取コードの再発行に失敗しました。',
    );
  }

  /** RPC戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;
  /** 再発行されたsecret-once ptコード */
  const rewardCode = String(firstRow?.reward_code ?? '');
  if (
    !firstRow?.rating_id
    || (rewardCode && !STALL_RATING_REWARD_CODE_PATTERN.test(rewardCode))
  ) {
    throwInvalidResponse(VISITOR_REISSUE_ANONYMOUS_STALL_RATING_REWARD_CODE_RPC_NAME);
  }
  return {
    ratingId: String(firstRow.rating_id),
    rewardPoints: Number(firstRow.reward_points ?? 0),
    rewardCode: rewardCode || null,
    rewardCodeHint: String(firstRow.reward_code_hint ?? ''),
    rewardExpiresAt: String(firstRow.reward_expires_at ?? ''),
    isReplay: !rewardCode,
  };
};

/**
 * ログイン済み来場者が匿名回答ptコードを交換する
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {string} rewardCode - 12文字pt受取コード
 * @returns {Promise<Object>} pt付与結果
 * @throws {AppError} 入力・状態・通信・レスポンスエラー時
 */
export const visitorClaimStallRatingReward = async (sessionToken, rewardCode) => {
  /** セッショントークン検証結果 */
  const sessionValidation = validateRequiredString(sessionToken, 'セッショントークン');
  if (!sessionValidation.isValid) {
    throw new AppError(sessionValidation.message, {
      code: VISITOR_STALL_RATING_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }
  /** 大文字化したpt受取コード */
  const normalizedRewardCode = String(rewardCode ?? '').trim().toUpperCase();
  if (!STALL_RATING_REWARD_CODE_PATTERN.test(normalizedRewardCode)) {
    throw new AppError('12文字のpt受取コードを入力してください。', {
      code: VISITOR_STALL_RATING_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();
  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(
    VISITOR_CLAIM_STALL_RATING_REWARD_RPC_NAME,
    {
      p_session_token: sessionToken.trim(),
      p_reward_code: normalizedRewardCode,
    },
  );
  if (error) {
    throwMappedStallRatingRpcError(
      VISITOR_CLAIM_STALL_RATING_REWARD_RPC_NAME,
      error,
      VISITOR_STALL_RATING_SUBMIT_FAILED_CODE,
      'pt受取コードの交換に失敗しました。',
    );
  }

  /** RPC戻り値の先頭行 */
  const firstRow = Array.isArray(data) ? data[0] : data;
  if (!firstRow?.rating_id || !firstRow?.stall_id || !firstRow?.stall_name) {
    throwInvalidResponse(VISITOR_CLAIM_STALL_RATING_REWARD_RPC_NAME);
  }

  // pt残高が変わるため、ホーム集約データのTTLキャッシュを破棄する
  invalidateVisitorHomeBootstrapCache(sessionToken.trim());

  return {
    ratingId: String(firstRow.rating_id),
    stallId: String(firstRow.stall_id),
    stallName: String(firstRow.stall_name),
    pointsGranted: Number(firstRow.points_granted ?? 0),
    currentPoint: Number(firstRow.current_point ?? 0),
    maxReachedPoint: Number(firstRow.max_reached_point ?? 0),
    pointLogId: String(firstRow.point_log_id ?? ''),
  };
};
